import { ErrorFill } from "@filament/react/icons/error-fill";
import { NotificationContent, NotificationHeader, NotificationRenderer, NotificationsQueue } from "@filament/react/notification";
import { Component, ReactNode } from "react";

interface Props {
    children: ReactNode;
    fallback?: (error?: Error) => ReactNode;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
    onReset?: () => void;
}
interface State {
    hasError: boolean;
    error?: Error;
}


export const ErrorStackTrace = ({ error }: { error: Error }) => {
    return (
        <div style={{ marginTop: '24px' }}>
            <details style={{
                background: '#fff3f3',
                border: '1px solid #fcc',
                borderRadius: '4px',
                padding: '12px'
            }}>
                <summary style={{
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    color: '#d32f2f'
                }}>
                    🐛 Error details (development only)
                </summary>
                <div style={{ marginTop: '12px' }}>
                    <p style={{
                        fontWeight: 'bold',
                        color: '#d32f2f',
                        marginBottom: '8px'
                    }}>
                        {error.message}
                    </p>
                    <pre style={{
                        fontSize: '11px',
                        overflow: 'auto',
                        background: '#f5f5f5',
                        padding: '12px',
                        borderRadius: '4px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                    }}>
                        {error.stack}
                    </pre>
                </div>
            </details>
        </div>
    )
}

export class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('ErrorBoundary caught error:', {
            error,
            errorInfo,
            componentStack: errorInfo.componentStack,
        });

        this.props.onError?.(error, errorInfo);

        NotificationsQueue.clear()
        NotificationsQueue.banner(
            () => (
                <>
                    <NotificationHeader><ErrorFill /> Something went wrong</NotificationHeader>
                    <NotificationContent>{error.message}</NotificationContent>
                </>
            ),
            { signal: 'error' }
        )

    }

    resetError = () => {
        this.setState({ hasError: false, error: undefined });
        this.props.onReset?.();
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        if (this.props.fallback) return this.props.fallback(this.state.error);

        return (
            <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
                <NotificationRenderer variant="banner" />

                {/* Additional error details in development */}
                {import.meta.env.DEV && this.state.error && (
                    <ErrorStackTrace error={this.state.error} />
                )}
            </div>
        );
    }
}